import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './DocumentUpload.css';

const DocumentUpload = ({ onUploadSuccess, onNavigateToReviews }) => {
  const { user, token, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    form_type: 'WH-347_Rev_December_2008',
    uploader_id: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-populate uploader_id when user is available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        uploader_id: user.username || user.email || user.user_id || ''
      }));
    }
  }, [user]);

  // No auto-redirect - let users read the message and navigate manually

  // Helper function to get proper authentication headers
  const getAuthHeaders = () => {
    // Priority: 1. User JWT token, 2. Environment variable, 3. Default fallback
    const authToken = (isAuthenticated() && token) 
      ? token 
      : (process.env.REACT_APP_API_AUTH_TOKEN || '12345');
      
    return {
      'Authorization': `Bearer ${authToken}`
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (file) => {
    if (file) {
      setSelectedFile(file);
      setError(null);
      // Auto-populate title if empty
      if (!formData.title) {
        const fileName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
        setFormData(prev => ({
          ...prev,
          title: fileName
        }));
      }
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleNavigateToReviews = () => {
    if (onNavigateToReviews) {
      onNavigateToReviews();
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.uploader_id.trim()) {
      setError('Uploader email/username is required');
      return false;
    }
    if (!selectedFile) {
      setError('Please select a file to upload');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      // Create FormData for file upload
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('title', formData.title);
      uploadFormData.append('form_type', formData.form_type);
      uploadFormData.append('uploader_id', formData.uploader_id);

      // Upload file
      const authHeaders = getAuthHeaders();

      const response = await axios.post('/documents/upload', uploadFormData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200 || response.status === 201) {
        // Check if N8N processing is available and adjust message accordingly
        const n8nAvailable = response.data.n8n_processing_available;
        const isLocalhost = response.data.environment_info?.is_localhost;
        
        let message = 'Document uploaded successfully';
        if (n8nAvailable) {
          message += ' and automated processing has been initiated';
        } else if (isLocalhost) {
          message += '. Note: Automated N8N processing is not available in local development. Please process the document manually through the review interface.';
        } else {
          message += '. Note: Automated processing is not available in this environment.';
        }

        setUploadResult({
          success: true,
          message: message,
          document_id: response.data.document_id,
          s3_path: response.data.s3_path,
          n8n_processing_available: n8nAvailable,
          environment_info: response.data.environment_info
        });

        // Reset form (preserve uploader_id if user is logged in)
        setFormData({
          title: '',
          form_type: 'WH-347_Rev_December_2008',
          uploader_id: user ? (user.username || user.email || user.user_id || '') : ''
        });
        setSelectedFile(null);

        // Call success callback if provided
        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadResult({
        success: false,
        message: err.response?.data?.detail || 'Upload failed. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="document-upload">
      <div className="upload-header">
        <h2>Upload Document for Processing</h2>
        <p>Upload a WH-347 payroll document to begin automated processing and review.</p>
      </div>

      <form onSubmit={handleSubmit} className="upload-form">
        {/* File Upload Area */}
        <div
          className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInputChange}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
          />

          {selectedFile ? (
            <div className="selected-file">
              <div className="file-icon">📄</div>
              <div className="file-info">
                <div className="file-name">{selectedFile.name}</div>
                <div className="file-size">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </div>
              </div>
              <button
                type="button"
                className="remove-file"
                onClick={() => setSelectedFile(null)}
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="upload-prompt">
              <div className="upload-icon">📤</div>
              <div className="upload-text">
                <p><strong>Drag and drop your document here</strong></p>
                <p>or</p>
                <button
                  type="button"
                  className="browse-button"
                  onClick={handleBrowseClick}
                >
                  Browse Files
                </button>
              </div>
              <div className="file-types">
                Supported formats: PDF, DOC, DOCX, JPG, PNG (Max: 100MB)
              </div>
            </div>
          )}
        </div>

        {/* Form Fields */}
        <div className="form-fields">
          <div className="form-group">
            <label htmlFor="title">Document Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter document title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="form_type">Form Type</label>
            <select
              id="form_type"
              name="form_type"
              value={formData.form_type}
              onChange={handleInputChange}
            >
              <option value="WH-347_Rev_December_2008">WH-347 Rev December 2008</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="uploader_id">Your Email/Username *</label>
            <input
              type="text"
              id="uploader_id"
              name="uploader_id"
              value={formData.uploader_id}
              onChange={handleInputChange}
              placeholder={user ? "Auto-filled from your account" : "Enter your email or username"}
              readOnly={!!user}
              className={user ? "auto-filled" : ""}
              required
            />
            {user && (
              <small className="field-note">
                ✓ Automatically filled from your logged-in account
              </small>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <div className={`upload-result ${uploadResult.success ? 'success' : 'error'}`}>
            <span className="result-icon">
              {uploadResult.success ? '✅' : '❌'}
            </span>
            <div className="result-message">
              {uploadResult.message}
              {uploadResult.document_id && (
                <div className="result-details">
                  Document ID: {uploadResult.document_id}
                </div>
              )}
              {uploadResult.success && uploadResult.environment_info?.is_localhost && (
                <div className="localhost-info">
                  <span className="info-icon">ℹ️</span>
                  <strong>Development Environment:</strong> To process this document, navigate to the review interface and manually trigger processing or use the API directly.
                </div>
              )}
              {uploadResult.success && uploadResult.n8n_processing_available === false && !uploadResult.environment_info?.is_localhost && (
                <div className="no-n8n-info">
                  <span className="warning-icon">⚠️</span>
                  <strong>Manual Processing Required:</strong> Automated document processing is not available in this environment.
                </div>
              )}
              {uploadResult.success && (
                <div className="navigation-info">
                  <span className="info-icon">📋</span>
                  <span>
                    Your document has been uploaded successfully.
                  </span>
                  <button 
                    type="button" 
                    className="view-reviews-btn"
                    onClick={handleNavigateToReviews}
                  >
                    View Document Reviews
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={uploading || !selectedFile}
          >
            {uploading ? (
              <>
                <span className="spinner">⏳</span>
                Uploading...
              </>
            ) : (
              'Upload & Process Document'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DocumentUpload;